import { showLoader, hideLoader, updateLoaderText, showMaintenanceOverlay, hideMaintenanceOverlay, state, showBackendDownOverlay, showUpstreamErrorOverlay, log, showDiagBanner, toast, showIdentityForbiddenOverlay } from './ui.js';

// Ідентичність кур'єра для запитів до /webapp/**.
//
// Раніше підставлявся ЛИШЕ courier_meest_id, і два різні збої давали
// «не знайдено кур'єра» на справному поштоматі:
//   • порожній meest_id (branches.html із кешу авторизації, де він не
//     зберігся) — запит летів узагалі без ідентифікації;
//   • застарілий meest_id (1С перевидала GUID, кеш клієнта не оновився) —
//     бекенд відмовляв, хоча tg_user_id у тому ж URL вказував на живий
//     обліковий запис.
// Тому шлемо ОБИДВА, коли обидва є: бекенд пробує їх по черзі
// (CourierResolver.resolve), meest_id лишається пріоритетним. Так само
// вже робить courier.html (courier/core/api.js).
export function withIdentityParam(path, search) {
  const params = new URLSearchParams(search || '');
  const meest = (params.get('courier_meest_id') || '').trim();
  const tgUserId = (params.get('tg_user_id') || '').trim();
  let out = path;
  if (meest && !out.includes('courier_meest_id=')) {
    out += (out.includes('?') ? '&' : '?') + 'courier_meest_id=' + encodeURIComponent(meest);
  }
  if (tgUserId && !out.includes('tg_user_id=')) {
    out += (out.includes('?') ? '&' : '?') + 'tg_user_id=' + encodeURIComponent(tgUserId);
  }
  return out;
}

// Чи є у launch-URL хоч якийсь ідентифікатор кур'єра. Використовується
// перед скануванням поштомату: без нього жоден запит не має сенсу, і
// краще сказати про це прямо, ніж отримати порожній список посилок.
export function hasCourierIdentity(search) {
  const params = new URLSearchParams(search || '');
  return Boolean(
    (params.get('courier_meest_id') || '').trim()
    || (params.get('tg_user_id') || '').trim(),
  );
}

function demoMock(path, fetchOpts = {}) {
  const method = typeof fetchOpts.method === 'string' ? fetchOpts.method.toUpperCase() : 'GET';
  // Provide deterministic mock data for demo mode
  if (path.startsWith('/settings')) {
    const g = window;
    if (!g.__DEMO_SETTINGS) {
      g.__DEMO_SETTINGS = { theme: 'dark', tsdMode: false, autoOpenCell: false, version: 1 };
    }
    const store = g.__DEMO_SETTINGS;
    if (method === 'POST') {
      try {
        const body = typeof fetchOpts.body === 'string' ? JSON.parse(fetchOpts.body) : fetchOpts.body || {};
        if (body && typeof body === 'object') {
          if ('theme' in body) store.theme = body.theme === 'light' ? 'light' : 'dark';
          if ('tsdMode' in body) store.tsdMode = Boolean(body.tsdMode);
          if ('autoOpenCell' in body) store.autoOpenCell = Boolean(body.autoOpenCell);
          if ('version' in body) {
            const ver = Number(body.version);
            if (Number.isFinite(ver) && ver >= store.version) {
              store.version = ver;
            } else {
              store.version += 1;
            }
          } else {
            store.version += 1;
          }
        }
      } catch (_) {}
    }
    return { ...store };
  }
  if (path.startsWith('/locker/by-qr')) {
    return {
      found: true,
      // Мережа — Епіцентр (демо-поштомат «як Епіцентр», див. free-cells нижче):
      // у демо діє правило «спершу посилки Епіцентру», як і на справжньому.
      branch: { branchID: 'DEMO-001', branchIDRef: '00000000-0000-0000-0000-DEMOLOCK', fullName: 'Демо Поштомат #1', networkPartnerCode: '230', networkId: 'epicentr' },
      courier: { courierID: 'DEMO-C', courierIDSQL: 'DEMO-C', name: 'Демо Кур’єр' },
    };
  }
  if (path.startsWith('/locker/parcels')) {
    return {
      postamatInfo: { freeCells: 7, totalCells: 24 },
      pickup: [
        { parcelID: 'PICK-001', parcelNumber: '7700101', barcode: 'PICK001BAR', agent: 'Епіцентр онлайн_поштомати' },
        { parcelID: 'PICK-002', parcelNumber: '7700102', barcode: 'PICK002BAR', agent: 'CAINIAO' },
      ],
      delivery: [
        { parcelID: 'DLV-101', parcelNumber: 'DL101', barcode: 'DLV101BAR', agent: 'Епіцентр онлайн_поштомати' },
        { parcelID: 'DLV-102', parcelNumber: 'DL102', barcode: 'DLV102BAR', agent: 'iHerb' },
        { parcelID: 'DLV-103', parcelNumber: 'DL103', barcode: 'DLV103BAR', agent: 'LPP' },
      ],
    };
  }
  if (path.startsWith('/locker/cells')) {
    return { total: 24 };
  }
  if (path.startsWith('/locker/free-cells')) {
    // Демо-поштомат «як Епіцентр»: усі пʼять розмірів, серед них один порожній —
    // саме на ньому видно погашену кнопку, заради якої сітка й переробляна.
    return {
      available: true,
      status: 'ok',
      actualOn: '2026-09-18T16:57:58+03:00',
      postamatType: 'EPIC',
      cells: [
        { size: 'XS', free: 10, height: 11, width: 18, depth: 68 },
        { size: 'S', free: 5, height: 18, width: 38, depth: 68 },
        { size: 'M', free: 4, height: 25, width: 38, depth: 68 },
        { size: 'L', free: 1, height: 40, width: 38, depth: 68 },
        { size: 'XL', free: 0, height: 60, width: 38, depth: 68 },
      ],
    };
  }
  if (path.startsWith('/locker/remove') && !path.includes('/confirm')) {
    return { result: { transID: 'TRANS-RM-1' } };
  }
  if (path.startsWith('/locker/remove/confirm')) {
    return { ok: true };
  }
  if (path.startsWith('/locker/insert') && !path.includes('/confirm')) {
    return { result: { transID: 'TRANS-IN-1', reservationID: 'RES-IN-1' } };
  }
  if (path.startsWith('/locker/insert/confirm')) {
    return { ok: true };
  }
  if (path.startsWith('/mini-sticker')) {
    return { ok: true };
  }
  if (path.startsWith('/locker/problem')) {
    return { ok: true };
  }
  if (path.startsWith('/courier/context')) {
    return { courierID: 'DEMO-C', courierIDSQL: 'DEMO-C', name: 'Демо Кур’єр' };
  }
  if (path.startsWith('/webapp/maintenance/status')) {
    return { active: false };
  }
  if (path.startsWith('/inventory/tasks')) {
    return {
      branchID: 'DEMO-001',
      branchIDRef: '00000000-0000-0000-0000-DEMOLOCK',
      branchAddress: 'вул. Демо, 1',
      branchNumber: '001',
      tasks: [
        {
          parcelId: 'INV-1',
          parcelNumber: 'INV123456',
          operationId: 'OP-INV-1',
          status: 'Очікує перевірки',
          resultOptions: ['Все гаразд', 'Посилка відсутня'],
          startedAt: '2024-01-01T12:00:00+00:00',
        },
        {
          parcelId: 'INV-2',
          parcelNumber: 'INV123457',
          operationId: 'OP-INV-2',
          status: 'Очікує перевірки',
          resultText: null,
          resultOptions: null,
        },
      ],
    };
  }
  if (path.startsWith('/inventory/result')) {
    return {
      ok: true,
      operation: {
        parcelId: 'INV-1',
        parcelNumber: 'INV123456',
        operationId: 'OP-INV-1',
        status: 'Все гаразд',
        completedAt: new Date().toISOString(),
      },
    };
  }
  return null;
}

async function checkMaintenanceOnce() {
  try {
    const r = await fetch('/webapp/maintenance/status', { credentials: 'include' });
    if (!r.ok) return { active: false };
    return r.json();
  } catch (_) {
    return { active: false };
  }
}

function sleep(ms) {
  return new Promise((res) => setTimeout(res, ms));
}

/**
 * Віддати мережевий збій модулю connectivity.js (глобал MeestConnectivity).
 * Він вирішить, чи справді сервер лежить: зробить кілька контрольних спроб і
 * покаже блокер лише тоді, коли жодна не пройшла. Якщо модуль не підвантажився
 * (стара розмітка з кешу) — повертає false, і виклик падає на старий шлях:
 * showBackendDownOverlay малює чесний текст без обіцянки автоперевірки і сам
 * вішає робочу кнопку, бо тут її більше нікому повісити.
 */
function reportConnectivityFailure(err) {
  const conn = typeof window !== 'undefined' ? window.MeestConnectivity : null;
  if (!conn || typeof conn.reportFailure !== 'function') {
    showBackendDownOverlay();
    return false;
  }
  return conn.reportFailure(err);
}

/**
 * Запит до `/webapp/**`.
 *
 * `silent: true` — для НЕОБОВʼЯЗКОВОЇ довідки, без якої екран працює далі
 * (напр. вільні комірки за розмірами). Вимикає геть усе, що api() показує
 * курʼєру сама: тост таймауту, блокер звʼязку (`reportConnectivityFailure`),
 * оверлей «Проблеми на стороні сервісу поштоматів», оверлей 403 і весь
 * maintenance-цикл із його 300 перевірками. Виклик просто кидає помилку,
 * а рішення, що з нею робити, лишається за тим, хто його зробив.
 * `timeoutMs` — власний фронтовий таймаут замість 40 с (довідці стільки чекати
 * нема сенсу: бекенд тримає на неї 3 с).
 */
export async function api(path, opts = {}) {
  const { noLoader, loaderText, silent, timeoutMs, ...fetchOpts } = opts || {};
  const base = window.location.origin;
  // Demo mode: intercept and return mocks without network
  if (state.demoMode) {
    const mock = demoMock(path, fetchOpts);
    if (mock != null) {
      if (!noLoader) showLoader(loaderText || 'Завантаження…');
      try {
        await new Promise((r) => setTimeout(r, 200));
        log('api:demo', { path });
        return mock;
      } finally {
        if (!noLoader) hideLoader();
      }
    }
  }
  // Auto-append the courier identity from the launch URL.
  let p = path;
  try {
    p = withIdentityParam(p, window.location.search);
  } catch (_) {
    // ignore
  }
  const url = base + '/webapp' + p;
  const startedAt = performance.now();
  const method = (fetchOpts && fetchOpts.method) || 'GET';
  log('api:request', { method, url });
  if (!noLoader) showLoader(loaderText || 'Завантаження…');
  try {
    // If maintenance modal is already shown (triggered elsewhere), wait until it ends to avoid spamming backend
    if (state.maintenance.visible && !silent) {
      let waits = 0;
      while (waits < 300) {
        waits++;
        await sleep(1000);
        const cur = await checkMaintenanceOnce();
        if (!cur.active) break;
      }
      hideMaintenanceOverlay();
    }
    let r;
    const controller = new AbortController();
    const fetchTimeout = setTimeout(() => controller.abort(), timeoutMs || 40000); // 40s frontend timeout
    try {
      r = await fetch(url, { ...fetchOpts, signal: controller.signal });
    } catch (e) {
      clearTimeout(fetchTimeout);
      if (e instanceof DOMException && e.name === 'AbortError') {
        log('api:timeout', { url });
        if (!silent) toast('Запит перевищив час очікування. Спробуйте ще раз.');
        // Помилку тегуємо явно: далі її класифікують за ознакою, а не за
        // текстом (той залежить від рушія і локалі).
        const timeoutErr = new Error('Request timeout');
        timeoutErr.kind = 'timeout';
        // Тиха перевірка: якщо сервер живий — нічого не показуємо.
        if (!silent) reportConnectivityFailure(timeoutErr);
        throw timeoutErr;
      }
      // Мережевий зрив. Раніше тут одразу малювався блокувальний екран, і
      // ОДНА невдала спроба на LTE лишала кур'єра перед «Бекенд недоступний»
      // на здоровому проді (інцидент 15.09.2026). Тепер рішення за
      // connectivity.js: він перевіряє сервер кількома спробами і сам ховає
      // екран, щойно звʼязок повернувся.
      log('api:network_error', { url, error: String(e) });
      try { e.kind = 'network'; } catch (_) { /* заморожена помилка */ }
      if (!silent) reportConnectivityFailure(e);
      throw e;
    }
    clearTimeout(fetchTimeout);
    if (!r.ok) {
      // Identity guard: forbidden without courier identity on public host
      if (r.status === 403) {
        let msg = 'Доступ заборонено';
        try {
          const d = await r.json();
          if (d && typeof d === 'object' && d.message) msg = d.message;
        } catch (_) {}
        if (!silent) showIdentityForbiddenOverlay(msg);
        throw new Error('HTTP 403');
      }
      // If we caught 503 directly from a guarded endpoint, show overlay and retry once
      if (r.status === 503 && !silent) {
        log('api:maintenance_503', { url });
        let msg = 'Тимчасове обслуговування', ends = null;
        try {
          const d = await r.json();
          if (d && typeof d === 'object') {
            msg = d.message || msg;
            ends = d.endsAt || null;
          }
          log('api:maintenance_payload', { active: d?.active, endsAt: d?.endsAt });
        } catch (_) { log('api:maintenance_payload_parse_failed'); }
        if (!state.operationActive) {
          showMaintenanceOverlay(msg, ends);
        } else {
          // defer showing until we exit operation
          state.maintenance.pending = { message: msg, endsAt: ends };
        }
        // Wait until endsAt if provided, else fixed backoff
        let waits = 0;
        while (waits < 300) {
          waits++;
          await sleep(1000);
          const cur = await checkMaintenanceOnce();
          if (!cur.active) break;
        }
        hideMaintenanceOverlay();
        const r2 = await fetch(url, fetchOpts);
        if (!r2.ok) throw new Error('HTTP ' + r2.status);
        log('api:retry_after_maintenance_ok', { status: r2.status });
        return r2.json();
      }
      // 502/504 — це відповідь ШЛЮЗУ, а не Meest: наш бекенд не піднявся або
      // саме рестартується (на проді таке вікно триває 30-90 с). Показувати
      // «Проблеми на стороні сервісу поштоматів» тут було неправдою, і той
      // оверлей не зникав сам. Віддаємо випадок у connectivity.js.
      if (r.status === 502 || r.status === 504) {
        log('api:gateway_error', { url, status: r.status });
        const gwErr = new Error('HTTP ' + r.status);
        gwErr.status = r.status;
        gwErr.kind = 'network';
        if (!silent) reportConnectivityFailure(gwErr);
        throw gwErr;
      }
      // Upstream/Server errors: show an overlay for user.
      // Але не тоді, коли вже показано блокер звʼязку: 5xx у такий момент — це
      // та сама аварія бекенда, а не проблема на боці Meest, і два оверлеї
      // одночасно означали б для кур'єра дві різні причини однієї події.
      if (r.status >= 500 || r.status === 424) {
        if (!silent && !window.MeestConnectivity?.controller?.blocked) {
          showUpstreamErrorOverlay('Проблеми на стороні сервісу поштоматів. Спробуйте пізніше.');
        }
      }
      // 4xx несе людське пояснення у detail (бекенд проксить message від
      // poststations). Голий Error('HTTP 400') лишав курʼєра перед відкритою
      // коміркою без жодної підказки, зарахувалась операція чи ні.
      let detail = '';
      try {
        const errBody = await r.json();
        detail = typeof errBody?.detail === 'string'
          ? errBody.detail
          : (errBody?.detail?.message || errBody?.message || '');
      } catch (_) { /* тіло не JSON */ }
      throw new Error(detail || ('HTTP ' + r.status));
    }
    const json = await r.json();
    const dur = Math.round(performance.now() - startedAt);
    log('api:response', { url, status: r.status, ms: dur });
    return json;
  } finally {
    if (!noLoader) hideLoader();
  }
}

export async function runWithLoader(fn, text, { immediate = false } = {}) {
  showLoader(text, { immediate });
  try {
    return await fn();
  } finally {
    hideLoader();
  }
}

export { updateLoaderText };
